import React, { useState } from 'react';
import { ChevronLeft, Sparkles, Check, MessageCircle, MapPin, BarChart3, Calendar, Warehouse, Trophy, Users, Bell, Settings, Mic, Eye, Share2, Zap, Crown, ArrowRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSubscription } from '../contexts/SubscriptionContext';
import { BetaBadge } from '../components/PageShell';
import SEO from '../components/SEO';

const PRO_FEATURES = [
  { icon: MessageCircle, label: 'Unlimited AI chats', desc: 'No daily message cap with Swell' },
  { icon: Mic, label: 'Speech-to-text', desc: 'Talk to Swell hands-free' },
  { icon: MapPin, label: 'Unlimited favorites', desc: 'Star as many spots as you want' },
  { icon: Eye, label: '7-day forecasts + webcams', desc: 'Plan ahead with extended data' },
  { icon: BarChart3, label: 'Full session analytics', desc: 'Trends, progression, comparisons' },
  { icon: Calendar, label: 'Calendar & trip planner', desc: 'AI scheduling and multi-spot trips' },
  { icon: Warehouse, label: 'Unlimited boards & suits', desc: 'No quiver size limits' },
  { icon: Trophy, label: 'Streak rescue + full leaderboards', desc: 'Save streaks and see full stats' },
  { icon: Share2, label: 'Shareable cards', desc: 'Flex your rank and streaks' },
  { icon: Users, label: 'Crew system', desc: 'Add friends, compare stats, challenges' },
  { icon: Bell, label: 'Smart weather alerts', desc: '"Tomorrow looks epic at your spot"' },
  { icon: Settings, label: 'Automations & integrations', desc: 'Custom triggers and external tools' },
];

export default function Billing() {
  const navigate = useNavigate();
  const { isPro, subscription, links, portal, refresh } = useSubscription();
  const [billing, setBilling] = useState('annual');

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-8 md:py-12 relative z-10">
      <SEO title="Swell Pro" description="Upgrade to Swell Pro for unlimited AI coaching, extended forecasts, full analytics, and more." path="/billing" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden min-h-[85vh] flex flex-col p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="billing-back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-lg font-heading font-bold text-white" data-testid="billing-title">
              {isPro ? 'Swell Pro' : 'Go Pro'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isPro ? 'Manage your subscription' : 'Unlock the full quiver'}
            </p>
          </div>
        </div>

        {/* Active Pro Status */}
        {isPro ? (
          <div className="rounded-2xl border border-white/5 overflow-hidden">
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-6 text-center border-b border-white/5">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-amber-500/20">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-lg font-heading font-bold text-white mb-1">Swell Pro Active</h2>
              <p className="text-xs text-muted-foreground">
                {subscription.interval === 'year' ? 'Annual plan' : 'Monthly plan'}
                {subscription.cancel_at_period_end && ' — cancels at period end'}
              </p>
              {subscription.current_period_end && (
                <p className="text-[10px] text-muted-foreground/60 mt-1">
                  {subscription.cancel_at_period_end ? 'Access until' : 'Renews'}: {new Date(subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="p-5">
              <a
                href={portal}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manage-subscription-btn"
                className="w-full flex items-center justify-center gap-2 bg-white/10 text-white text-sm font-medium py-3 rounded-xl border border-white/10 hover:bg-white/15 transition-colors"
              >
                Manage Subscription <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="text-[10px] text-muted-foreground/50 text-center mt-3">
                Update payment, change plan, or cancel via Stripe portal
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Plan Card */}
            <div className="rounded-2xl border border-white/5 overflow-hidden mb-4">
              {/* Pricing Header */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-6 text-center border-b border-white/5">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-amber-500/20">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-lg font-heading font-bold text-white mb-1">Swell Pro</h2>
                <p className="text-xs text-muted-foreground/80 mb-4">The Full Quiver</p>

                {/* Toggle */}
                <div className="flex items-center justify-center gap-1 bg-white/5 rounded-full p-1 max-w-[240px] mx-auto mb-4" data-testid="billing-toggle">
                  <button
                    data-testid="billing-monthly"
                    onClick={() => setBilling('monthly')}
                    className={`flex-1 text-xs py-2 rounded-full font-medium transition-all ${billing === 'monthly' ? 'bg-white/15 text-white' : 'text-muted-foreground hover:text-white/70'}`}
                  >
                    Monthly
                  </button>
                  <button
                    data-testid="billing-annual"
                    onClick={() => setBilling('annual')}
                    className={`flex-1 text-xs py-2 rounded-full font-medium transition-all ${billing === 'annual' ? 'bg-white/15 text-white' : 'text-muted-foreground hover:text-white/70'}`}
                  >
                    Annual
                  </button>
                </div>

                {/* Price */}
                <div className="mb-2">
                  {billing === 'annual' ? (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-heading font-bold text-white">$99</span>
                        <span className="text-sm text-muted-foreground">/year</span>
                      </div>
                      <p className="text-[11px] text-emerald-400 font-medium mt-1">
                        $8.25/mo — save 37% vs monthly
                      </p>
                    </>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-heading font-bold text-white">$13</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 mb-3 font-medium">Everything in Free, plus:</p>
                <div className="space-y-2.5 mb-5">
                  {PRO_FEATURES.map((f, i) => (
                    <motion.div
                      key={f.label}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <f.icon className="w-3 h-3 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-white">{f.label}</p>
                        <p className="text-[10px] text-muted-foreground/60">{f.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                {(billing === 'annual' ? links.annual : links.monthly) ? (
                <a
                  href={billing === 'annual' ? links.annual : links.monthly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="subscribe-btn"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold py-3.5 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-amber-500/20"
                >
                  <Sparkles className="w-4 h-4" />
                  {billing === 'annual' ? 'Go Pro — $99/year' : 'Go Pro — $13/month'}
                  <ArrowRight className="w-4 h-4" />
                </a>
                ) : (
                <div className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-muted-foreground text-sm font-medium py-3.5 rounded-xl">
                  Pro subscriptions coming soon
                </div>
                )}
                <p className="text-[10px] text-muted-foreground/40 text-center mt-3">
                  Cancel anytime. Secure checkout via Stripe.
                </p>
              </div>
            </div>

            {/* Free Tier Summary */}
            <div className="bg-bg-paper/60 backdrop-blur-xl rounded-2xl border border-white/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/40 mb-2 font-medium">Dawn Patrol (Free)</p>
              <div className="space-y-1.5 text-xs text-muted-foreground/60">
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> 13 AI messages per day</div>
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> 7 sessions per month</div>
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> 3 favorite spots, 3 boards, 2 suits</div>
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> Live conditions + 24hr forecast</div>
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> Streaks, aura points, basic leaderboard</div>
                <div className="flex items-center gap-2"><Check className="w-3 h-3 text-primary/50" /> Referral system (always free)</div>
              </div>
            </div>
          </>
        )}
        <BetaBadge />
      </motion.div>
    </div>
  );
}
